import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import moment from 'moment';
import { MessageService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Table, TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, forkJoin, Observable, of } from 'rxjs';
import { Application } from '../../../model/Application';
import { HttpResponse } from '../../../model/HttpResponse';
import { ProcessStatus } from '../../../model/ProcessStage';
import { QueryOptionDto } from '../../../model/QueryOptionDto';
import { ApplicationType } from '../../../model/enum/ApplicationType';
import { ApplicationService } from '../../../services/application.service';
import { DocumentsService } from '../../../services/documents.service';
import { LoadingService } from '../../../services/loading.service';
import { ReportService } from '../../../services/report.service';
import { RoleService } from '../../../services/role.service';
import { SolicitudesTableComponent } from '../../gestor/solicitudes/solicitudes-table/solicitudes-table.component';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';

@Component({
  selector: 'app-financial-vinculaciones',
  standalone: true,
  templateUrl: './financial-vinculaciones.component.html',
  styleUrl: './financial-vinculaciones.component.scss',
  providers: [MessageService],
  imports: [
    TabViewModule,
    BadgeModule,
    AvatarModule,
    CommonModule,
    PageHeaderComponent,
    TableModule,
    ButtonModule,
    ToolbarModule,
    ToastModule,
    InputTextModule,
    TooltipModule,
    DialogModule,
    SolicitudesTableComponent,
    SelectButtonModule,
    FormsModule,
    InputTextareaModule,
    TagModule,
  ],
})
export class FinancialVinculacionesComponent implements OnInit {
  tabs = [
    {
      label: 'Validaciones Pendientes',
      value: 'MANUAL_PAYMENTS',
    },
    {
      label: 'Historial de Verificaciones',
      value: 'VERIFICATION_HISTORY',
    },
  ];
  modalitieTabIndex: number = 0;

  tabSelected: string = 'MANUAL_PAYMENTS';

  applicationWithManualPayment: Application[] = [];
  showPaymenManualValidation: boolean = false;
  applicationsByPayment: any[] = [];
  documentPreviewSelected: any = null;
  showDocumentPreview: boolean = false;
  showVinculationDetails: boolean = false;
  applicationsByPaymentSelected: Application[] = [];
  /** Grupo de pago que se está consultando en el modal de solicitudes */
  paymentGroupSelected: any = null;
  showRevisionConceptModal: boolean = false;
  paymentStatusOptions: any[] = [
    { label: 'APROBADO', value: ProcessStatus.APPROVED },
    { label: 'NO APROBADO', value: ProcessStatus.REJECTED },
  ];
  paymentRejectReason: string = '';

  paymentStatusSelected: string = 'approved';

  // ======================================
  // 📚 Historial de verificaciones
  // ======================================
  showVerificationHistory: boolean = false;
  verifiedPayments: any[] = [];
  filteredVerifiedPayments: any[] = [];
  historyStatusFilter: string = 'ALL';
  historyStatusOptions: any[] = [
    { label: 'Todos', value: 'ALL' },
    { label: 'Aprobados', value: 'APPROVED' },
    { label: 'Rechazados', value: 'REJECTED' },
  ];

  constructor(
    private applicationService: ApplicationService,
    private loadingService: LoadingService,
    private messageService: MessageService,
    private documentsService: DocumentsService,
    private sanitizer: DomSanitizer,
    private roleService: RoleService,
    private reportService: ReportService
  ) {}

  ngOnInit(): void {
    this.onModalitieTabChange({ index: this.modalitieTabIndex });
  }

  onModalitieTabChange(event: any) {
    this.tabSelected = this.tabs[event.index].value;
    if (this.tabSelected === 'MANUAL_PAYMENTS') {
      this.getVinculationWithManualPayment();
      this.showPaymenManualValidation = true;
      this.showVerificationHistory = false;
    }
    if (this.tabSelected === 'VERIFICATION_HISTORY') {
      this.showPaymenManualValidation = false;
      this.getVerifiedPayments();
    }
  }

  getVinculationWithManualPayment() {
    this.loadingService.showLoading();
    const queryOptions: QueryOptionDto = {
      options: {
        conditionals: [
          {
            conditionalConnector: 'AND',
            evaluatedProperty: 'process.processStage',
            logicOperator: '=',
            parameterName: '@ManualPayment',
            parameterValue: 'payment_manual_validation',
          },
          {
            conditionalConnector: 'AND',
            evaluatedProperty: 'payment.paymentStatus',
            logicOperator: '<>',
            parameterName: '@PaymentStatus',
            parameterValue: 'payment_manually_approved',
          },
        ],
      },
    };
    this.applicationService
      .getApplications(queryOptions)
      .subscribe((response: HttpResponse) => {
        if (response.code === 200) {
          this.applicationWithManualPayment =
            response.response as Application[];
          this.groupByApplicationByPayment();
        }
        this.loadingService.hideLoading();
      });
  }

  groupByApplicationByPayment() {
    this.applicationsByPayment = [];
    this.applicationWithManualPayment.forEach((application: Application) => {
      const club = application.club;
      const paymentId = application.payment!.paymentId;

      const clubIndex = this.applicationsByPayment.findIndex(
        (clubItem) => clubItem.paymentId === paymentId
      );
      if (clubIndex === -1) {
        this.applicationsByPayment.push({
          clubId: club.id,
          clubName: club.name,
          paymentId: paymentId,
          applications: [application],
        });
      } else {
        this.applicationsByPayment[clubIndex].applications.push(application);
      }
    });
  }

  /**
   * Consulta las solicitudes cuyo pago manual ya fue verificado (aprobado o rechazado),
   * para conservar la trazabilidad después de emitir el concepto.
   */
  getVerifiedPayments() {
    this.loadingService.showLoading();

    const verifiedStatuses = [
      'payment_manually_approved',
      ProcessStatus.PAYMENT_MANUAL_REJECTED,
    ];

    const requests: Observable<HttpResponse>[] = verifiedStatuses.map(
      (paymentStatus) => {
        const queryOptions: QueryOptionDto = {
          options: {
            conditionals: [
              {
                conditionalConnector: 'AND',
                evaluatedProperty: 'payment.paymentStatus',
                logicOperator: '=',
                parameterName: '@PaymentStatus',
                parameterValue: paymentStatus,
              },
            ],
          },
        };
        return this.applicationService
          .getApplications(queryOptions)
          .pipe(
            catchError(() => of({ code: 204, response: [] } as HttpResponse))
          );
      }
    );

    forkJoin(requests).subscribe({
      next: (responses: HttpResponse[]) => {
        const applications = responses.reduce(
          (accumulated: Application[], response: HttpResponse) =>
            response.code === 200 && Array.isArray(response.response)
              ? accumulated.concat(response.response as Application[])
              : accumulated,
          []
        );
        this.groupVerifiedPayments(applications);
        this.showVerificationHistory = true;
        this.loadingService.hideLoading();
      },
      error: () => {
        this.groupVerifiedPayments([]);
        this.showVerificationHistory = true;
        this.loadingService.hideLoading();
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No fue posible consultar el historial de verificaciones',
          life: 4000,
        });
      },
    });
  }

  /** Agrupa las solicitudes verificadas por pago y precalcula cantidades y valores */
  groupVerifiedPayments(applications: Application[]) {
    const groups: any[] = [];

    for (const application of applications) {
      const paymentId = application.payment?.paymentId;
      if (!paymentId) {
        continue;
      }
      let group = groups.find((item) => item.paymentId === paymentId);
      if (!group) {
        group = {
          paymentId: paymentId,
          clubId: application.club?.id,
          clubName: application.club?.name ?? 'Club no identificado',
          applications: [],
        };
        groups.push(group);
      }
      group.applications.push(application);
    }

    for (const group of groups) {
      const newApplications = group.applications.filter(
        (application: Application) => application.type === ApplicationType.NEW
      );
      const renewalApplications = group.applications.filter(
        (application: Application) =>
          application.type === ApplicationType.RENEWAL
      );
      const reference: Application = group.applications[0];

      group.newCount = newApplications.length;
      group.newAmount = this.calculateAmountOfApplications(newApplications);
      group.renewalCount = renewalApplications.length;
      group.renewalAmount =
        this.calculateAmountOfApplications(renewalApplications);
      group.totalAmount = group.newAmount + group.renewalAmount;
      group.applicationsCount = group.applications.length;
      group.paymentStatus = reference.payment?.paymentStatus ?? '';
      group.isApproved = group.paymentStatus === 'payment_manually_approved';
      group.statusLabel = group.isApproved ? 'Aprobado' : 'Rechazado';
      group.statusSeverity = group.isApproved ? 'success' : 'danger';
      group.verificationDate =
        reference.auditData?.modifiedDate ??
        reference.payment?.paymentDate ??
        reference.auditData?.createdDate ??
        'Sin registro';
      group.verifiedBy = reference.auditData?.modifiedBy ?? 'No registrado';
      group.comments = reference.comments ?? '';
    }

    this.verifiedPayments = groups.sort((a, b) =>
      `${b.verificationDate}`.localeCompare(`${a.verificationDate}`)
    );
    this.applyHistoryFilter();
  }

  applyHistoryFilter() {
    this.filteredVerifiedPayments = this.verifiedPayments.filter((group) => {
      if (this.historyStatusFilter === 'APPROVED') {
        return group.isApproved;
      }
      if (this.historyStatusFilter === 'REJECTED') {
        return !group.isApproved;
      }
      return true;
    });
  }

  private calculateAmountOfApplications(applications: Application[]): number {
    return applications.reduce((total: number, application: Application) => {
      const applicationTotal =
        application.payment?.paymentItems?.reduce(
          (itemsTotal: number, paymentItem) =>
            itemsTotal + (paymentItem.valorTotal ?? 0),
          0
        ) ?? 0;
      return total + applicationTotal;
    }, 0);
  }

  getRegistrarions(paymentId: string, applicationType: string) {
    const application = this.applicationsByPayment.find((application) => {
      return application.paymentId === paymentId;
    });
    if (application) {
      return application.applications.filter((application: Application) => {
        return application.type === applicationType;
      });
    } else {
      return [];
    }
  }

  calculateAmount(paymentId: string, applicationType: string) {
    const application = this.getRegistrarions(paymentId, applicationType);
    return application.reduce((acc: any, application: Application) => {
      return (
        acc +
        application.payment?.paymentItems.reduce((acc, paymentItem) => {
          return acc + paymentItem.valorTotal;
        }, 0)
      );
    }, 0);
  }

  calculateTotalAmount(clubId: string) {
    return (
      this.calculateAmount(clubId, ApplicationType.NEW) +
      this.calculateAmount(clubId, ApplicationType.RENEWAL)
    );
  }

  openApplications(aplicationByPayment: any) {
    // El grupo recibido ya trae sus solicitudes, así sirve tanto para las
    // validaciones pendientes como para el historial de verificaciones.
    this.paymentGroupSelected = aplicationByPayment;
    this.applicationsByPaymentSelected = aplicationByPayment.applications;
    this.showVinculationDetails = true;
  }

  /** Exporta a Excel las solicitudes asociadas al pago que se está consultando */
  exportApplicationsOfPayment() {
    const dataToPrint = this.applicationsByPaymentSelected.map(
      (application: Application) => ({
        'Tipo Solicitud':
          application.type === ApplicationType.NEW
            ? 'Nueva Vinculación'
            : 'Renovación de Vinculación',
        Club: application.club?.name ?? '',
        Modalidad: application.modalidad ?? '',
        Nombres: application.applicant?.name?.toUpperCase() ?? '',
        Apellidos: application.applicant?.lastName?.toUpperCase() ?? '',
        'Tipo Identificación': application.applicant?.idType ?? '',
        Identificación: application.applicant?.idNumber ?? '',
        Correo: application.applicant?.contactInformation?.email ?? '',
        Estado: this.getSeverity(application.process?.processStage ?? ''),
        'Fecha Solicitud': this.getHumanTime((application as any).created_at),
        Valor:
          application.payment?.paymentItems?.reduce(
            (total: number, paymentItem) => total + (paymentItem.valorTotal ?? 0),
            0
          ) ?? 0,
        'Referencia Pago': application.payment?.paymentId ?? '',
      })
    );

    const reportName = `Vinculaciones del pago ${
      this.paymentGroupSelected?.clubName ?? ''
    } ${moment().format('DD-MM-YYYY')}`;

    this.reportService.exportExcel(
      [{ data: dataToPrint, sheetName: 'Vinculaciones' }],
      reportName.trim()
    );
  }

  validateApplication(applicationByPayment: any) {
    this.applicationsByPaymentSelected = applicationByPayment.applications;
    this.showRevisionConceptModal = true;
  }

  showSupport(applicationByPayment: any) {
    this.loadingService.showLoading();
    this.documentsService
      .getDocument(applicationByPayment.paymentId)
      .subscribe((res: HttpResponse) => {
        this.loadingService.hideLoading();
        if (res.response.id) {
          this.documentPreviewSelected = {
            documentName: `Soporte de pago ${applicationByPayment.clubName}_${applicationByPayment.paymentId}`,
            base64: this.sanitizer.bypassSecurityTrustResourceUrl(
              res.response.filestream
            ),
          };
          this.showDocumentPreview = true;
        } else {
          this.documentPreviewSelected = null;
          this.showDocumentPreview = false;

          this.messageService.add({
            severity: 'warn',
            summary: 'Sin Carnet Digital',
            detail: 'No se ha encontrado carnet Digital',
            life: 3000,
          });
        }
      });
  }

  getSeverity(status: string) {
    switch (status) {
      case 'created':
        return 'PENDIENTE REVISIÓN';
      case 'approved':
        return 'APROBADA';
      case 'responsed':
        return 'RESPUESTA ENVIADA';
      case 'responsed_rejected':
        return 'RESPUESTA RECHAZADA ENVIADA';
      case 'responsed_approved':
        return 'RESPUESTA APROBADA ENVIADA';
      case 'rejected':
        return 'RECHAZADA';
      case 'finished':
        return 'FINALIZADA';
      case 'payment_started':
        return 'En proceso de pago';
      case 'finished_approved':
        return 'Vinculado';
      case 'finished_declined':
        return 'Finalizado pago rechazado';
      case 'finished_error':
        return 'Finalizado error de pago';
      case 'finished_payment_rejected':
        return 'Finalizado pago rechazado';
      case 'accepted_payment_pending':
        return 'Aprobado - Pendiente de Pago';
      case 'finished_payment_error':
        return 'Error en Pago';
      case 'payment_manual_validation':
        return 'Pago en Revisión Manual';
      case 'payment_abandoned':
        return 'Pago Abandonado';

      default:
        return 'Estado Desconocido';
    }
  }

  getHumanTime(time: number) {
    return moment(time).format('DD/MM/yyyy hh:mm:ss a');
  }

  clear(table: Table) {
    table.clear();
  }

  onCancelPaymentResponse() {
    this.applicationsByPaymentSelected = [];
    this.showRevisionConceptModal = false;
  }

  onConfirmPaymentResponse() {
    this.loadingService.showLoading();
    // Se deja constancia de quién y cuándo emitió el concepto para el historial
    const verifiedBy = this.roleService.getUserName();
    const verificationDate = moment().format('YYYY-MM-DD HH:mm:ss');
    const applicationsToUpdate = this.applicationsByPaymentSelected.map(
      (application: Application) => {
        application.auditData = {
          ...application.auditData,
          modifiedBy: verifiedBy,
          modifiedDate: verificationDate,
        };
        if (this.paymentStatusSelected === ProcessStatus.REJECTED) {
          application.process!.processStage = ProcessStatus.PAYMENT_MANUAL_REJECTED;
          application.payment!.paymentStatus = ProcessStatus.PAYMENT_MANUAL_REJECTED;
          application.comments = this.paymentRejectReason;
          return application;
        }
        application.payment!.paymentStatus = 'payment_manually_approved';
        return application;
      }
    );
    let totalApplicationUpdated = 0;
    for (let applicationToUpdate of applicationsToUpdate) {
      this.applicationService
        .updateApplication(applicationToUpdate)
        .subscribe((response: HttpResponse) => {
          if (response.code === 200) {
            totalApplicationUpdated++;
            if (totalApplicationUpdated === applicationsToUpdate.length) {
              this.messageService.add({
                severity: 'success',
                summary: 'Solicitudes Actualizadas',
                detail:
                  'La respuesta de verificación del pago de las vinculaciones han sido enviadas correctamente',
                life: 3000,
              });
              this.applicationsByPaymentSelected = [];
              this.showRevisionConceptModal = false;
              this.getVinculationWithManualPayment();
              this.loadingService.hideLoading();
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error al actualizar el pago de las solicitudes',
              detail: 'Ha ocurrido un error al actualizar las solicitudas',
              life: 3000,
            });
            this.loadingService.hideLoading();
          }
        });
    }
  }
}
